
import { currencyFormatter } from "components/KPIs/helpers";
import { tripsAPI } from "components/Apis/trips";
import SearchableMultiSelect from "components/UI/Atoms/inputs/selects/searchableMultiselect";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTrips } from "redux/logistics/trips/tripsSlice";
import { useDebouncedValue } from "utils/logic";

const FILTER_FIELDS = [
    { key: "external_id", label: "Trip #" },
    { key: "pickup", label: "Pickup (ZIP/City/State/Location)" },
    { key: "delivery", label: "Delivery (ZIP/City/State/Location)" },
];

const DEFAULT_STATUS_OPTIONS = [
    { value: "dispatched", label: "Dispatched" },
    { value: "in_progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
];

const DEFAULT_VEHICLE_SIZE_OPTIONS = [
    { value: "Cargo Van", label: "Cargo Van" },
    { value: "Sprinter", label: "Sprinter" },
    { value: "Cube Van", label: "Cube Van" },
    { value: "Dock High Straight", label: "Dock High Straight" },
    { value: "Tractor", label: "Tractor" },
    { value: "Flatbed", label: "Flatbed" },
    { value: "3.5", label: "Rabon 3.5 Ton" },
    { value: "Rabon", label: "Rabon" },
    { value: "Reefer", label: "Reefer" },
    { value: "Caja", label: "Caja" },
];

const DEFAULT_PENDING_FILTERS = {
    status: ["completed"],
};

const sanitizeFilters = (filters) =>
    Object.entries(filters).reduce((acc, [key, value]) => {
        if (value === null || value === undefined) return acc;
        if (Array.isArray(value)) {
            const normalized = value.map((item) => String(item).trim()).filter(Boolean);
            if (!normalized.length) return acc;
            acc[key] = normalized.join(",");
            return acc;
        }
        if (typeof value === "string" && value.trim() === "") return acc;
        acc[key] = typeof value === "string" ? value.trim() : value;
        return acc;
    }, {});

const parseNextPageFromUrl = (nextUrl) => {
    if (!nextUrl) return null;
    try {
        const parsed = new URL(nextUrl, window.location.origin);
        const value = Number(parsed.searchParams.get("page"));
        return Number.isFinite(value) && value > 0 ? value : null;
    } catch {
        return null;
    }
};

const Trips = () => {
    const dispatch = useDispatch();
    const { trips, loading, error, count, next, pageSize } = useSelector((state) => state.trips);
    const [page, setPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState(() => sanitizeFilters(DEFAULT_PENDING_FILTERS));
    const [pendingFilters, setPendingFilters] = useState(DEFAULT_PENDING_FILTERS);
    const [localPageSize, setLocalPageSize] = useState(pageSize || 10);
    const [expandedStops, setExpandedStops] = useState({});
    const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);
    const [vehicleSizeOptions, setVehicleSizeOptions] = useState(DEFAULT_VEHICLE_SIZE_OPTIONS);
    const loadMoreRef = useRef(null);
    const pagingRef = useRef(false);
    const debouncedFilters = useDebouncedValue(pendingFilters, 500);

    useEffect(() => {
        dispatch(fetchTrips({ page, page_size: localPageSize, filters: activeFilters, append: page > 1 }));
    }, [dispatch, page, localPageSize, activeFilters]);

    useEffect(() => {
        if (!loading) {
            pagingRef.current = false;
        }
    }, [loading]);

    useEffect(() => {
        let mounted = true;
        tripsAPI.getFilterOptions()
            .then((response) => {
                if (!mounted) return;
                if (Array.isArray(response?.status) && response.status.length > 0) {
                    setStatusOptions(response.status);
                }
                if (Array.isArray(response?.vehicle_size) && response.vehicle_size.length > 0) {
                    setVehicleSizeOptions(response.vehicle_size);
                }
            })
            .catch(() => {
                // keep fallback options
            });
        return () => {
            mounted = false;
        };
    }, []);

    const handleFilterChange = (key, value) => {
        setPendingFilters((prev) => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        const sanitized = sanitizeFilters(debouncedFilters);
        if (JSON.stringify(sanitized) === JSON.stringify(activeFilters)) return;
        setActiveFilters(sanitized);
        pagingRef.current = false;
        setPage(1);
    }, [debouncedFilters, activeFilters]);

    const handlePageSizeChange = (event) => {
        const nextSize = Number(event.target.value);
        setLocalPageSize(nextSize);
        pagingRef.current = false;
        setPage(1);
    };

    useEffect(() => {
        if (!loadMoreRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;
                if (!next || loading || pagingRef.current) return;
                const nextPage = parseNextPageFromUrl(next);
                if (!nextPage || nextPage === page) return;
                pagingRef.current = true;
                setPage(nextPage);
            },
            { rootMargin: "200px" }
        );
        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [next, loading, page]);

    const toggleStops = (stopKey) => {
        setExpandedStops((prev) => ({
            ...prev,
            [stopKey]: !prev[stopKey],
        }));
    };

    return (
        <div className="trips-page">
            <header className="trips-header">
                <div>
                    <p className="trips-subtitle">Filter, browse, and review trip details.</p>
                </div>
                <div className="trips-summary">
                    <span>Total: {count || 0}</span>
                    <span>
                        Loaded: {trips.length}
                    </span>
                </div>
            </header>

            <section className="trips-filters">
                <div className="trips-filters__grid">
                    {FILTER_FIELDS.map((field) => (
                        <label key={field.key} className="trips-filter">
                            <span className="trips-filter__label">{field.label}</span>
                            <input
                                className="trips-filter__input"
                                type="text"
                                value={pendingFilters[field.key] || ""}
                                onChange={(event) => handleFilterChange(field.key, event.target.value)}
                                placeholder={
                                    field.key === "external_id" ? "e.g. 253169" : "e.g. 37040, Clarksville"
                                }
                            />
                        </label>
                    ))}
                    <label className="trips-filter">
                        <span className="trips-filter__label">Trip Status</span>
                        <SearchableMultiSelect
                            options={statusOptions}
                            value={pendingFilters.status || []}
                            onChange={(vals) => handleFilterChange("status", Array.isArray(vals) ? vals : vals ? [vals] : [])}
                            placeholder="Select status"
                            chipLimit={3}
                        />
                    </label>
                    <label className="trips-filter">
                        <span className="trips-filter__label">Vehicle Size</span>
                        <SearchableMultiSelect
                            options={vehicleSizeOptions}
                            value={pendingFilters.vehicle_size || []}
                            onChange={(vals) => handleFilterChange("vehicle_size", Array.isArray(vals) ? vals : vals ? [vals] : [])}
                            placeholder="Select vehicle size"
                            chipLimit={3}
                        />
                    </label>
                </div>
                <div className="trips-filters__actions">
                    <label className="trips-page-size">
                        <span>Page size</span>
                        <select value={localPageSize} onChange={handlePageSizeChange} disabled={loading}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </label>
                </div>
            </section>

            <section className="trips-list">
                {loading && page === 1 && <div className="trips-status">Loading trips...</div>}
                {error && !loading && <div className="trips-status trips-status--error">{String(error)}</div>}
                {!loading && !error && trips.length === 0 && (
                    <div className="trips-status">No trips found for the selected filters.</div>
                )}
                {!error && trips.length > 0 && (
                    <div className="trips-table">
                        <div className="trips-table__head">
                            <div>Trip</div>
                            <div>Status</div>
                            <div>Assets / Carrier</div>
                            <div>Stops</div>
                            <div>Vehicle Size</div>
                            <div>Pay</div>
                            <div>Stops Detail</div>
                        </div>
                        <div className="trips-table__body">
                            {trips.map((trip) => {
                                const tripKey = trip.id || trip.external_id;
                                const vehicleUnit = trip?.vehicle?.unit_no || trip?.vehicle?.vehicle_no || "—";
                                const trailerNo = trip?.trailer?.trailer_no || "—";
                                const driverNames = Array.isArray(trip?.drivers)
                                    ? trip.drivers.map((driver) => driver?.name).filter(Boolean).join(", ")
                                    : "";
                                const stops = Array.isArray(trip?.stops) ? trip.stops : [];
                                const orderedStops = [...stops].sort((a, b) => {
                                    const aSeq = a?.seq ?? 0;
                                    const bSeq = b?.seq ?? 0;
                                    return aSeq - bSeq;
                                });
                                const carrierName = trip?.carrier?.name || "—";
                                const payAmount = Number(trip?.pay) || 0;
                                const pay = currencyFormatter.format(payAmount);
                                const totalDistance = stops.reduce((acc, stop) => acc + (Number(stop?.distance_mi) || 0), 0);
                                const distanceDisplay = totalDistance > 0
                                    ? `${totalDistance.toLocaleString(undefined, { maximumFractionDigits: 1 })} mi`
                                    : "— mi";
                                const ratePerMileDisplay = totalDistance > 0
                                    ? `${currencyFormatter.format(payAmount / totalDistance)}/mi`
                                    : "—/mi";
                                const coverType = (trip?.cover_type || "").toLowerCase();
                                const isCarrier = coverType === "carrier";
                                const isAsset = coverType === "asset";
                                return (
                                    <div key={tripKey} className="trips-table__row">
                                        <div>{trip.external_id || trip.id || "—"}</div>
                                        <div>{trip.status || "—"}</div>
                                        <div>
                                            {isAsset && (
                                                <>
                                                    <div>Vehicle: {vehicleUnit}</div>
                                                    <div>Trailer: {trailerNo}</div>
                                                    <div>Drivers: {driverNames || "—"}</div>
                                                </>
                                            )}
                                            {isCarrier && (
                                                <>
                                                    <div>Carrier: {carrierName}</div>
                                                    <div>Phone: {trip?.carrier?.phone_no || "—"}</div>
                                                    <div>Trailer ID: {trip?.carrier?.trailer_id || "—"}</div>
                                                    <div>Tracking No: {trip?.carrier?.tracking_no || "—"}</div>
                                                </>
                                            )}
                                            {!isAsset && !isCarrier && "—"}
                                        </div>
                                        <div className="trips-stops-summary">
                                            <div>{stops.length}</div>
                                            <div className="trips-stops-summary__miles">{distanceDisplay}</div>
                                        </div>
                                        <div>{trip.vehicle_size || "—"}</div>
                                        <div className="trips-rate">
                                            <div className="trips-rate__pay">{pay}</div>
                                            <div className="trips-rate__meta">
                                                <span className="trips-rate__badge">{ratePerMileDisplay}</span>
                                            </div>
                                        </div>
                                        <div className="trips-table__stops">
                                            {orderedStops.length === 0 && "—"}
                                            {orderedStops.length > 0 && (
                                                <div className="trips-table__stop-chips">
                                                    {orderedStops.map((stop) => {
                                                        const stopKey = stop.id || `${tripKey}-stop-${stop.seq}`;
                                                        const stopOpen = expandedStops[stopKey] ?? false;
                                                        return (
                                                            <div key={stopKey} className={`trips-table__chip ${stopOpen ? "is-open" : ""}`}>
                                                                <div
                                                                    className={`trips-table__chip-label ${stopOpen ? "is-open" : ""}`}
                                                                    title={!stopOpen ? `Stop #${stop.seq || "—"} · ${stop.location_name || "—"}` : undefined}
                                                                >
                                                                    #{stop.seq || "—"} {stop.location_name || "—"}
                                                                </div>
                                                                <div className="trips-table__chip-time">
                                                                    {stop.arrival_time || stop.scheduled_arrival_time || "—"}
                                                                </div>
                                                                <button
                                                                    className="trips-table__chip-toggle"
                                                                    type="button"
                                                                    onClick={() => toggleStops(stopKey)}
                                                                    aria-expanded={stopOpen}
                                                                    aria-label={stopOpen ? "Hide stop details" : "Show stop details"}
                                                                >
                                                                    <i className={`fa ${stopOpen ? "fa-chevron-up" : "fa-chevron-down"}`} />
                                                                </button>
                                                                {stopOpen && (
                                                                    <div className="trips-table__chip-details">
                                                                        <div className="trips-table__stop-section">
                                                                            <div className="trips-table__stop-section-title">Timing</div>
                                                                            <div className="trips-table__stop-section-body">
                                                                                <div>Initial Scheduled: {stop.original_scheduled_arrival_time || "—"}</div>
                                                                                <div>Scheduled: {stop.scheduled_arrival_time || "—"}</div>
                                                                                <div>ETA: {stop.estimated_arrival_time || "—"}</div>
                                                                                <div>Arrived: {stop.arrival_time || "—"}</div>
                                                                                <div>Departed: {stop.departure_time || "—"}</div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="trips-table__stop-section">
                                                                            <div className="trips-table__stop-section-title">Location</div>
                                                                            <div className="trips-table__stop-section-body">
                                                                                <div>{stop.location_name || "—"}</div>
                                                                                <div>
                                                                                    {stop.city || "—"}, {stop.state || "—"} {stop.zip_code || ""}
                                                                                </div>
                                                                                <div>Status: {stop.status || "—"}</div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="trips-table__stop-section">
                                                                            <div className="trips-table__stop-section-title">Orders</div>
                                                                            {Array.isArray(stop.load_events) && stop.load_events.length > 0 ? (
                                                                                <div className="trips-table__stop-orders">
                                                                                    {stop.load_events.map((event, index) => (
                                                                                        <div key={`${stopKey}-load-${index}`} className="trips-table__stop-order">
                                                                                            <span>
                                                                                                {event?.type ? event.type.toUpperCase() : "EVENT"} · Order #
                                                                                                {event?.order?.order_no || "—"} {event?.order?.order_group || ""}{" "}
                                                                                                {event?.order?.backorder || ""}
                                                                                            </span>
                                                                                            <span>Units: {event?.order?.units ?? "—"}</span>
                                                                                            <span>Pieces: {event?.pieces ?? "—"}</span>
                                                                                            <span>Weight: {event?.weight ?? "—"}</span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="trips-table__stop-empty">No order data.</div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>
            {loading && page > 1 && <div className="trips-status">Loading more trips...</div>}
            <div ref={loadMoreRef} className="trips-scroll-sentinel" aria-hidden="true" />
        </div>
    );
};

export default Trips;
